import type { AcceptanceDefaults, DocumentFieldType, DocumentTypeField } from '@/types/bundle'
import { fromPercent, toPercent } from '@/lib/confidence'
import { isValidIdentifier } from '@/lib/identifierValidation'

export { fromPercent, toPercent }

const SYNTHETIC_KEY_PREFIX = '__new_'

// Client-only placeholder key for an unsaved new/duplicated field - the
// fields record key is otherwise always equal to response_tag (confirmed
// against real bundle data), but a new/duplicated field has no response_tag
// yet. Never sent to the backend: resolveFieldsForSave remaps it before Save.
export function createSyntheticFieldKey(): string {
  return `${SYNTHETIC_KEY_PREFIX}${crypto.randomUUID()}`
}

export function isSyntheticFieldKey(key: string): boolean {
  return key.startsWith(SYNTHETIC_KEY_PREFIX)
}

export function createBlankField(): DocumentTypeField {
  return { response_tag: '', field_type: 'text', prompt_hints: [] }
}

// "Duplicate" pre-fills every setting except response_tag, which starts empty
// and required (Design PRD 04 AC5) - it's the field's stable per-field API
// identifier, so two fields sharing one would be a real correctness bug, the
// same "must not copy verbatim" treatment cloneForDuplicate applies at the
// document-type level.
export function duplicateField(source: DocumentTypeField): DocumentTypeField {
  const cloned = structuredClone(source)
  cloned.response_tag = ''
  return cloned
}

// A field with no explicit min_confidence override inherits the resolved
// platform default - critical fields use the (stricter) critical default, a
// document type's own override takes precedence over the global one.
export function resolveAcceptanceDefault(
  field: DocumentTypeField,
  entryDefaults: Partial<AcceptanceDefaults> | undefined,
  globalDefaults: AcceptanceDefaults,
): number {
  if (field.critical) {
    return entryDefaults?.critical_min_confidence_default ?? globalDefaults.critical_min_confidence_default
  }
  return entryDefaults?.min_confidence_default ?? globalDefaults.min_confidence_default
}

export const FIELD_TYPE_LABELS: Record<DocumentFieldType, string> = {
  text: 'Text',
  money: 'Money',
  date_iso: 'Date',
  boolean: 'Boolean',
  person_name: 'Person name',
  structured_address: 'Structured address',
}

export interface ResolvedFieldsForSave {
  fields: Record<string, DocumentTypeField>
  duplicateKeys: string[]
  emptyKeys: string[]
  invalidKeys: string[]
}

// Remaps every synthetic (unsaved new/copied) field key to its own
// response_tag value - the shape the backend actually expects. Never
// silently overwrites: an empty, invalid-shaped, or colliding response_tag
// is reported instead of resolved, so the caller can block Save on it
// (invalid-shaped per PRD 34 - reported here so it's visible before Save,
// not only after a rejected Publish).
//
// A blank response_tag is flagged for EVERY field, not just synthetic-keyed
// ones (PRD 35, defense in depth): a non-synthetic key is always non-empty
// (it's a real, previously-saved field id), so before this check a blank
// response_tag on such a field was invisible to Save entirely - effectiveKey
// fell back to the key itself and field.response_tag was never even read.
// That's the real incident's root cause (cloneForDuplicate used to blank
// response_tag while keeping the original, non-synthetic key) - this check
// stays as a backstop even after that's fixed, in case any other path or any
// already-corrupted draft leaves a field in the same state.
export function resolveFieldsForSave(fields: Record<string, DocumentTypeField>): ResolvedFieldsForSave {
  const resolved: Record<string, DocumentTypeField> = {}
  const emptyKeys: string[] = []
  const duplicateKeys: string[] = []
  const invalidKeys: string[] = []

  for (const [key, field] of Object.entries(fields)) {
    const synthetic = isSyntheticFieldKey(key)
    const effectiveKey = synthetic ? field.response_tag.trim() : key
    const blankResponseTag = synthetic ? effectiveKey.length === 0 : field.response_tag.trim().length === 0

    if (blankResponseTag) {
      emptyKeys.push(key)
      continue
    }
    if (!isValidIdentifier(effectiveKey)) {
      invalidKeys.push(key)
      continue
    }
    if (effectiveKey in resolved) {
      duplicateKeys.push(key)
      continue
    }
    resolved[effectiveKey] = field
  }

  return { fields: resolved, duplicateKeys, emptyKeys, invalidKeys }
}
